// src/components/dashboard/AlertsDropdown.jsx
import React, { useState, useEffect, useRef } from 'react';
import { fetchAlerts, clearAlert, clearAllAlerts } from '../../services/api.service';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/components/alerts.css';

const AlertsDropdown = () => {
  const [alerts, setAlerts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());
  const dropdownRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    const loadAlerts = async () => {
      if (user && user.Email) {
        await fetchUserAlerts();
      }
    };

    loadAlerts();
    
    // Set up interval to refresh alerts
    const intervalId = setInterval(loadAlerts, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(intervalId);
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fetchUserAlerts = async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem('authToken');
      if (!token || !user || !user.Email) return;
      
      const alertsData = await fetchAlerts(user.Email, token);
      // Sort alerts by created_at date, newest first
      const sortedAlerts = (alertsData || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setAlerts(sortedAlerts);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      setAlerts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearAlert = async (alertId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const token = sessionStorage.getItem('authToken');
      if (!token || !user || !user.Email) return;
      
      await clearAlert(alertId, user.Email, token);
      setAlerts(alerts.filter(alert => alert.id !== alertId));
      // Remove from expanded alerts if it was expanded
      setExpandedAlerts(prev => {
        const newSet = new Set(prev);
        newSet.delete(alertId);
        return newSet;
      });
    } catch (error) {
      console.error('Failed to clear alert:', error);
    }
  };

  const handleClearAllAlerts = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const token = sessionStorage.getItem('authToken');
      if (!token || !user || !user.Email) return;
      
      await clearAllAlerts(user.Email, token);
      setAlerts([]);
      setExpandedAlerts(new Set()); // Clear expanded state
    } catch (error) {
      console.error('Failed to clear all alerts:', error);
    }
  };

  const handleAlertClick = (alertId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setExpandedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
    
    if (!isOpen) {
      fetchUserAlerts();
    }
  };

  return (
    <li className="nav-item dropdown no-arrow mx-1" ref={dropdownRef}>
      <a 
        className="nav-link dropdown-toggle" 
        href="#" 
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
      >
        <div className="bell-icon-container">
          <i className="fas fa-bell fa-fw"></i>
          {alerts.length > 0 && (
            <span className="badge badge-danger badge-counter">
              {alerts.length > 9 ? '9+' : alerts.length}
            </span>
          )}
        </div>
      </a>
      
      {isOpen && (
        <div className="dropdown-list dropdown-menu dropdown-menu-right shadow animated--grow-in">
          <h6 className="dropdown-header">
            Alerts Center
          </h6>
          <div className="alerts-container">
            {isLoading ? (
              <div className="dropdown-item text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : alerts.length > 0 ? (
              <>
                {alerts.map(alert => {
                  const isExpanded = expandedAlerts.has(alert.id);
                  return (
                    <a 
                      key={alert.id} 
                      className={`dropdown-item d-flex align-items-start alert-item ${isExpanded ? 'expanded' : ''}`}
                      href="#"
                      onClick={(e) => handleAlertClick(alert.id, e)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="mr-3">
                        <div className="alert-icon-circle">
                          <i className="fas fa-bell"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="alert-date">{formatDate(alert.created_at)}</div>
                        <div className={`alert-message ${isExpanded ? 'expanded' : ''}`} title={alert.message}>
                          {isExpanded ? (
                            <p className="mb-0" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                              {alert.message}
                            </p>
                          ) : (
                            <span style={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {alert.message}
                            </span>
                          )}
                        </div>
                        {!isExpanded && alert.message.length > 80 && (
                          <small className="text-muted">Click to expand...</small>
                        )}
                      </div>
                      <div 
                        className="alert-dismiss" 
                        onClick={(e) => handleClearAlert(alert.id, e)}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="fas fa-times"></i>
                      </div>
                    </a>
                  );
                })}
                
                <div className="dropdown-divider"></div>
                <a 
                  className="dropdown-item text-center clear-all" 
                  href="#"
                  onClick={handleClearAllAlerts}
                >
                  <i className="fas fa-trash-alt mr-1"></i>
                  Clear All Alerts
                </a>
              </>
            ) : (
              <div className="dropdown-item text-center no-alerts">
                <i className="fas fa-bell-slash mb-2"></i>
                <p>No new alerts</p>
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export default AlertsDropdown;